/**
 * JsonLd — renders JSON-LD structured data into the document <head>.
 * Uses the json-ld builder utilities to assemble schema.org objects.
 */

import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import type { JsonLdComposition, ResolvedSeoMeta } from "@/types/seo";
import {
  buildJsonLdSchema,
  type JsonLdProductData,
  type JsonLdBreadcrumb,
} from "@/utils/json-ld";

interface JsonLdProps {
  composition: JsonLdComposition | undefined;
  seo: ResolvedSeoMeta;
  productData?: JsonLdProductData;
  breadcrumbs?: JsonLdBreadcrumb[];
}

export function JsonLd({
  composition,
  seo,
  productData,
  breadcrumbs,
}: JsonLdProps) {
  const schemas = useMemo(
    () => buildJsonLdSchema(composition, seo, { productData, breadcrumbs }),
    [composition, seo, productData, breadcrumbs]
  );

  if (schemas.length === 0) return null;

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
